import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconCheck, IconCircle, IconLoader2 } from '@tabler/icons-react'
import classNames from 'classnames'
import { isNil } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Progress } from '@/components/progress/Progress'

import { cancelOptimization } from '@/reducers/promptimizer/promptimizer.actions'
import { RUN_STATUS, SAMPLING_STAGE, selectRun } from '@/reducers/promptimizer/promptimizer.redux'

const STAGE = { DONE: 'done', ACTIVE: 'active', WAITING: 'waiting' }

const STAGE_ICON = {
  [STAGE.DONE]: IconCheck,
  [STAGE.ACTIVE]: IconLoader2,
  [STAGE.WAITING]: IconCircle,
}

// How much of the bar each stage owns. The first two have real denominators — eligible calls against the target,
// then calls labelled out of calls sampled. The last has nothing to count against: candidates are persisted in one
// write at the end, so `nCandidates` jumps 0 → N. That segment holds at its boundary and pulses rather than
// inching along on a made-up number.
const SAMPLE_SHARE = 0.3
const LABEL_SHARE = 0.45
const OPTIMIZE_START = SAMPLE_SHARE + LABEL_SHARE

const fractionOf = (done, total) => (total > 0 ? Math.min(done / total, 1) : 0)

const progressFraction = ({ status, progress }) => {
  if (status === RUN_STATUS.DONE) {
    return 1
  }

  if (progress.nSampled === 0) {
    // Still sampling. The sampler's own counts are the only thing moving, and until it reports any there is
    // nothing to show but the fact that something started.
    return 0.02 + SAMPLE_SHARE * fractionOf(progress.sampling.nEligible, progress.sampling.nTarget)
  }

  if (progress.nLabeled < progress.nSampled) {
    return SAMPLE_SHARE + LABEL_SHARE * fractionOf(progress.nLabeled, progress.nSampled)
  }

  return OPTIMIZE_START
}

// True while the CURRENT stage has nothing to count against, so the bar pulses rather than reading as frozen.
// Which stage that is changes as the run moves: sampling has a denominator only once the sampler reports its
// target, labelling always has one, and the optimizer never does.
const isIndeterminate = ({ status, progress }) => {
  if (status === RUN_STATUS.DONE) {
    return false
  }

  if (progress.nSampled === 0) {
    return isNil(progress.sampling.nTarget)
  }

  return progress.nLabeled >= progress.nSampled
}

/** What the sampler is doing right now, in its own words, while it has no rows for anyone else to read. */
const samplingLabel = ({ stage, nFilterMatched, nChecked, nEligible, nTarget }) => {
  if (stage === SAMPLING_STAGE.GATING) {
    return `Checking which calls this criterion applies to — ${nEligible} of ${nTarget} so far, ${nChecked} of ${nFilterMatched} checked`
  }

  if (stage === SAMPLING_STAGE.ANSWERING) {
    return `Reading the ${nEligible} calls we'll measure on`
  }

  return 'Finding the calls this scorecard covers'
}

const stageState = (done, active) => {
  if (done) {
    return STAGE.DONE
  }

  return active ? STAGE.ACTIVE : STAGE.WAITING
}

/** The stages a run passes, in order, each derived from rows the pipeline has already written. */
const buildStages = ({ status, progress }) => {
  const sampled = progress.nSampled > 0
  const labelled = progress.nLabeled > 0 && progress.nLabeled >= progress.nSampled
  const finished = status === RUN_STATUS.DONE

  return [
    {
      key: 'sample',
      state: stageState(sampled, true),
      label: sampled
        ? `Found ${progress.nSampled} calls to measure on`
        : samplingLabel(progress.sampling),
    },
    {
      key: 'label',
      state: stageState(labelled, sampled),
      label:
        sampled && !labelled
          ? `Working out the right answers — ${progress.nLabeled} of ${progress.nSampled}`
          : 'Working out the right answers',
    },
    {
      key: 'optimize',
      state: stageState(finished, sampled && labelled),
      label:
        progress.nCandidates > 0
          ? `Writing and testing new versions — ${progress.nCandidates} so far`
          : 'Writing and testing new versions',
    },
  ]
}

/**
 * The run, while it is running. Expands in place under the prompt rather than taking over a modal, so the text
 * being optimized stays visible above it and nothing has to be closed and found again.
 *
 * Rendered from the moment of the click, before the run has an id — starting one is two sequential round trips,
 * and waiting for them leaves the button looking dead. Stop appears once there is something to stop.
 */
export const OptimizeRunPanel = ({ criteriaScid }) => {
  const dispatch = useDispatch()
  const { optimizationId, status, progress, loading, error } = useSelector((state) =>
    selectRun(state, criteriaScid)
  )

  const stages = buildStages({ status, progress })
  const fraction = progressFraction({ status, progress })

  return (
    <div className="copilot-optimize__panel">
      <div className="copilot-optimize__panel-header">
        <span className="copilot-optimize__panel-title">Optimizing this prompt</span>
        <span className="copilot-optimize__panel-note">
          This takes a few minutes. You can keep working — it doesn&apos;t need this page open.
        </span>
      </div>

      <Progress
        magical
        className="copilot-optimize__progress"
        value={Math.round(fraction * 100)}
        indeterminate={isIndeterminate({ status, progress })}
      />

      <ol className="copilot-optimize__stages">
        {stages.map((stage) => {
          const Icon = STAGE_ICON[stage.state]

          return (
            <li
              key={stage.key}
              className={classNames('copilot-optimize__stage', `is-${stage.state}`)}
            >
              <Icon className="copilot-optimize__stage-icon" />
              <span>{stage.label}</span>
            </li>
          )
        })}
      </ol>

      {error?.kind === 'pollFailed' && (
        <ErrorMessage
          warning
          content="Lost contact with the run for a moment. It is still going — this will catch up."
        />
      )}

      {!isNil(optimizationId) && (
        <Button
          small
          onClick={() => dispatch(cancelOptimization({ optimizationId, criteriaScid }))}
          loading={loading.cancel}
        >
          Stop
        </Button>
      )}
    </div>
  )
}
